import * as Yup from 'yup';

export const loginSchema = Yup.object().shape({
  email: Yup.string().min(1, 'Minimum email length is 1').max(190, 'Maximum email length is 190').required('Email is required'),
  password: Yup.string().min(8, 'Minimum password length is 8').max(30, 'Maximum password length is 30').required('Password is required'),
});

export const addUserSchema = Yup.object().shape({
  name: Yup.string().required("Name is required").min(3, "Minimum name length is 3"),
  designation: Yup.string().required("Name is required").min(3, "Minimum name length is 3"),
  phone: Yup.string().matches(/^[0-9]{10}$/, 'Invalid mobile number').required('Mobile number is required'),
  email: Yup.string().min(1, "Minimum email length is 1").max(190, "Maximum email length is 190").required("Email is required"),
  bank_id: Yup.string().required("Bank is required"),
  branch_id: Yup.string().required("Branch is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  password: Yup.string().min(8, "Minimum password length is 8").max(30, "Maximum password length is 30").required("Password is required"),
});

export const addUserUpdateSchema = Yup.object().shape({
  name: Yup.string().required("Name is required").min(3, "Minimum name length is 3"),
  designation: Yup.string().required("Name is required").min(3, "Minimum name length is 3"),
  phone: Yup.string().matches(/^[0-9]{10}$/, 'Invalid mobile number').required('Mobile number is required'),
  email: Yup.string().min(1, "Minimum email length is 1").max(190, "Maximum email length is 190").required("Email is required"),
  bank_id: Yup.string().required("Bank is required"),
  branch_id: Yup.string().required("Branch is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
});

export const addBranchSchema = Yup.object().shape({
  branch_name: Yup.string().required("Branch Name is required"),
  branch_city: Yup.string().required("Branch City is required"),
})

export const addBankSchema = Yup.object().shape({
  bank_name: Yup.string().required("Bank Name is required"),
  logo: Yup.mixed()
    .required("Bank logo is required") // Ensures the file is selected
    .test("fileSize", "File size should be less than 5MB", (value) => {
      if (typeof value === "string") return true;
      return value && value.size <= 5 * 1024 * 1024;
    })
    .test("fileType", "Only JPG, PNG, and GIF files are allowed", (value) => {
      if (typeof value === "string") return true;
      return (
        value && ["image/jpeg", "image/png", "image/gif"].includes(value.type)
      );
    }),
});

export const registerSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').min(3, 'Minimum name length is 3'),
  mobile_number: Yup.string().required('Mobile number is required'),
  email: Yup.string().min(1, 'Minimum email length is 1').max(190, 'Maximum email length is 190').required('Email is required'),
  bank_id: Yup.string().required('Bank is required'),
  city_id: Yup.string().required('City is required'),
  branch_name: Yup.string().required('Branch is required'),
  password: Yup.string().min(8, 'Minimum password length is 8').max(30, 'Maximum password length is 30').required('Password is required'),
  password_confirmation: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match').required('Confirm password is required'),
});

export const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().min(1, 'Minimum email length is 1').max(190, 'Maximum email length is 190').required('Email is required'),
});

export const ResetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'Minimum password length is 8')
    .max(30, 'Maximum password length is 30')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

export const passwordSchema = Yup.object().shape({
  current_password: Yup.string().required('Current password is required'),
  new_password: Yup.string()
    .min(8, 'Minimum password length is 8')
    .max(30, 'Maximum password length is 30')
    .required('New password is required'),
  new_password_confirmation: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

export const addBannerSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(3, "Minimum banner name length is 3")
    .max(100, "Maximum banner name length is 100")
    .required("Banner name is required"),

  context: Yup.string()
    .oneOf(
      [
        "feed",
        "tpdp",
        "jantri",
        "marketplace",
        "profile",
        "property",
        "land",
        "loan_calculator",
        "unit_conveter",
      ],
      "Invalid context"
    )
    .required("Context is required"),

  placement_key: Yup.string()
    .oneOf(["top", "middle", "bottom"], "Invalid placement")
    .required("Placement is required"),

  status: Yup.boolean().required("Status is required"),

  action_type: Yup.string()
    .oneOf(["url", "none", ""], "Invalid action type")
    .nullable()
    .notRequired(),

  action_value: Yup.string().when("action_type", {
    is: "url",
    then: (schema) => schema.url("Invalid URL").required("URL is required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  image: Yup.mixed().when("hasExistingImage", {
    is: false,
    then: (schema) =>
      schema
        .required("Banner image is required")
        .test(
          "fileType",
          "Only JPG, PNG, and WEBP images are allowed",
          (value) =>
            value &&
            ["image/jpeg", "image/png", "image/webp"].includes(value.type)
        )
        .test(
          "fileSize",
          "Image size must be less than 5MB",
          (value) => value && value.size <= 5 * 1024 * 1024
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const addIntroductionScreenSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(3, "Minimum title length is 3")
    .max(100, "Maximum title length is 100")
    .required("Title is required"),
  description: Yup.string()
    .trim()
    .max(500, "Maximum description length is 500"),
  button_text: Yup.string()
    .trim()
    .max(50, "Maximum button text length is 50"),
  platform: Yup.string()
    .oneOf(["android", "ios", "all", "web"], "Invalid platform")
    .required("Platform is required"),
  screen_order: Yup.number()
    .min(0, "Order must be at least 0")
    .notRequired(),
  skip_enabled: Yup.boolean().required("Skip enabled is required"),
  cta_enabled: Yup.boolean().required("CTA enabled is required"),
  cta_text: Yup.string().when("cta_enabled", {
    is: true,
    then: (schema) => schema.required("CTA text is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  cta_action: Yup.string().when("cta_enabled", {
    is: true,
    then: (schema) => schema.required("CTA action is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  is_active: Yup.boolean().required("Status is required"),
  image: Yup.mixed().when("hasExistingImage", {
    is: false,
    then: (schema) =>
      schema
        .required("Screen Image is required")
        .test(
          "fileType",
          "Only JPG, PNG, and WEBP images are allowed",
          (value) =>
            value &&
            ["image/jpeg", "image/png", "image/webp"].includes(value.type)
        )
        .test(
          "fileSize",
          "Image size must be less than 5MB",
          (value) => value && value.size <= 5 * 1024 * 1024
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});



export const addPropertySchema = Yup.object().shape({
  // bank_id: Yup.string().required("Bank is required"),
  // branch_id: Yup.string().required("Branch is required"),
  assigned_to: Yup.string().required("User Selection is required"),
  asset_category: Yup.string().required("Category is required"),
  property_type: Yup.string().required("Property is required"),
  property_address: Yup.string().required("Address is required"),
  taluka: Yup.string().required("Taluka is required"),
  // city: Yup.string().required("City is required"),
  // state: Yup.string().required("State is required"),
  province_state: Yup.string().required("State is required"),
  city_town: Yup.string().required("City is required"),
  area_locality: Yup.string().required("Category is required"),
  // status:Yup.string().required("Status is required"),
  land_area: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "Land area must be a valid number")
    .required("Land area is required"),
  builtup_carpet_area: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "carpet area must be a valid number")
    .required("carpet area is required"),
  borrower_names: Yup.string().required("Borrower Names is required"),
  possession_type: Yup.string().required("Possession Type is required"),
  description: Yup.string().required("Description is required"),
  country: Yup.string().required("Country is required"),
  reserve_price: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "Reserve price must be a valid number")
    .required("Reserve price is required"),
  emd: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "Emd must be a valid number")
    .required("Emd is required"),
  min_increment: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "Minimum Increment must be a valid number")
    .required("Minimum Increment is required"),
  contact_number: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "Contact Number must be a valid number")
    .required("Contact Number is required"),
  amount_outstanding: Yup.string()
    .matches(/^\d+(\.\d+)?$/, "Amount outstanding must be a valid number")
    .required("Amount outstanding is required"),
  dsc_required: Yup.boolean().required("This field is required"),
  contact_name: Yup.string().required("Contact Name is required"),
  auction_type: Yup.string().required("Auction Type is required"),
  auction_start_time: Yup.string().required(
    "Auction Start date & time is required"
  ),
  auction_end_time: Yup.string()
    .required("Auction End date & time is required")
    .test(
      "is-after-start",
      "Auction End date & time must be after Auction Start date & time",
      function (value) {
        const { auction_start_time } = this.parent; // Access sibling fields
        if (!value || !auction_start_time) return true; // Skip if either field is empty
        return new Date(value) > new Date(auction_start_time); // Ensure end time is after start time
      }
    ),
  // auction_end_time: Yup.string().required('Auction End date & time is required'),
  time_extension: Yup.string().required("Type Extension is required"),
  application_submission_date: Yup.string().required('Application Submission date & time is required'),
  property_inspection_datetime: Yup.string().required("Property Inspection date & time is required"),
  
  service_provider: Yup.string().required("Service Provider is required"),
  
  property_document: Yup.mixed()
    .required("PDF file is required")
    .test("fileType", "Only PDF files are allowed", (value) => {
      // Skip validation for existing file paths (string values)
      if (typeof value === "string") {
        return true;
      }
      // Validate only for new file uploads
      return value && value.type === "application/pdf";
    })
    .test("fileSize", "File size must be less than 5 MB", (value) => {
      // Skip validation for existing file paths (string values)
      if (typeof value === "string") {
        return true;
      }
      // Validate only for new file uploads
      return value && value.size <= 5 * 1024 * 1024;
    }),
});

// MEP SCHEMA ENDS

// const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif']; // Allowed image types
// const MAX_FILE_SIZE = 1024 * 1024; // 1MB in bytes

export const categorySchema = Yup.object().shape({
  category_name: Yup.string().required('Name is required'),
  page_title: Yup.string().required('Page title is required'),
  page_sub_title: Yup.string().required('Page Sub title is required'),
  category_video_link: Yup.string().required('Video link is required'),
  category_image: Yup.mixed().required("Image is required"),
  image_alt: Yup.string().required('Image alt is required'),
  category_banner_image: Yup.mixed().required("Banner Image is required"),
  category_description: Yup.string().required('Description is required'),
  meta_title: Yup.string().required('Meta title is required'),
  meta_description: Yup.string().required('Meta description is required'),
  meta_keyword: Yup.string().required('Meta keyword is required'),
  
});

export const categoryUpdateSchema = Yup.object().shape({
  category_name: Yup.string().required('Name is required'),
  page_title: Yup.string().required('Page title is required'),
  page_sub_title: Yup.string().required('Page Sub title is required'),
  image_alt: Yup.string().required('Image alt is required'),
  category_video_link: Yup.string().required('Video link is required'),
  // category_image: Yup.mixed().required("Image is required"),
  // category_banner_image: Yup.mixed().required("Banner Image is required"),
  category_description: Yup.string().required('Description is required'),
  meta_title: Yup.string().required('Meta title is required'),
  meta_description: Yup.string().required('Meta description is required'),
  meta_keyword: Yup.string().required('Meta keyword is required'),
  // default_category_description: Yup.string().required('Default description is required'),
});

export const subCategorySchema = Yup.object().shape({
  sub_category_name: Yup.string().required('Name is required'),
  sub_category_video_link: Yup.string().required('Video link is required'),
  sub_category_image: Yup.mixed().required("Image is required"),
  sub_category_banner_image: Yup.mixed().required("Banner Image is required"),
  sub_category_description: Yup.string().required('Description is required'),
  meta_title: Yup.string().required('Meta title is required'),
  meta_description: Yup.string().required('Meta description is required'),
  meta_keyword: Yup.string().required('Meta keyword is required'),
  default_sub_category_description: Yup.string().required('Default description is required'),
  default_meta_title: Yup.string().required('Default meta title is required'),
  default_meta_description: Yup.string().required('Default meta description is required'),
  default_meta_keyword: Yup.string().required('Default meta keyword is required'),
});

export const subCategoryUpdateSchema = Yup.object().shape({
  sub_category_name: Yup.string().required('Name is required'),
  sub_category_video_link: Yup.string().required('Video link is required'),
  // sub_category_image: Yup.mixed().required("Image is required"),
  // sub_category_banner_image: Yup.mixed().required("Banner Image is required"),
  sub_category_description: Yup.string().required('description is required'),
  meta_title: Yup.string().required('Meta title is required'),
  meta_description: Yup.string().required('Meta description is required'),
  meta_keyword: Yup.string().required('Meta keyword is required'),
  default_sub_category_description: Yup.string().required('Default description is required'),
  default_meta_title: Yup.string().required('Default meta title is required'),
  default_meta_description: Yup.string().required('Default meta description is required'),
  default_meta_keyword: Yup.string().required('Default meta keyword is required'),
});

export const blogSchema = Yup.object().shape({
  blog_title: Yup.string().required('Title is required'),
  author: Yup.string().required('Author is required'),
  blog_image: Yup.mixed().required("Image is required"),
  blog_description: Yup.string().required('Description is required'),
});

export const blogUpdateSchema = Yup.object().shape({
  blog_title: Yup.string().required('Title is required'),
  author: Yup.string().required('Author is required'),
  blog_description: Yup.string().required('Description is required'),
});

export const gallerySchema = Yup.object().shape({
  gallery_title: Yup.string().required('Title is required'),
  gallery_image: Yup.mixed().required("Image is required"),
});

export const galleryUpdateSchema = Yup.object().shape({
  gallery_title: Yup.string().required('Title is required'),
});

export const testimonialSchema = Yup.object().shape({
  client_name: Yup.string().required('Name is required'),
  rating: Yup.number().required('Rating is required'),
  client_address: Yup.string().required('Address is required'),
  client_message: Yup.string().required('Message is required'),
  client_image: Yup.mixed().required("Image is required"),
});

export const testimonialUpdateSchema = Yup.object().shape({
  client_name: Yup.string().required('Name is required'),
  rating: Yup.number().required('Rating is required'),
  client_address: Yup.string().required('Address is required'),
  client_message: Yup.string().required('Message is required'),
  // client_image: Yup.mixed().required("Image is required"),
});

export const settingsSchema = Yup.object().shape({
  customer_care_no: Yup.string().min(1, 'Minimum Customer Care No length is 1').max(15, 'Maximum Customer Care No length is 15').required('Customer Care No is required'),
  company_name: Yup.string().min(1, 'Minimum Company Name length is 1').max(50, 'Maximum Company Name length is 50').required('Company Name is required'),
  ceo_name: Yup.string().min(1, 'Minimum CEO Name length is 1').max(50, 'Maximum CEO Name length is 50').required('CEO Name is required'),
  description: Yup.string().min(5, 'Minimum Description length is 5').max(500, 'Maximum Description length is 500').required('Description is required'),
  website_email: Yup.string().min(1, 'Minimum email length is 1').max(190, 'Maximum email length is 190').required('Website email is required'),
  address: Yup.string().min(5, 'Minimum Address length is 5').max(300, 'Maximum Address length is 300').required('Company Address is required'),
  facebook_link: Yup.string().min(1, 'Minimum Facebook Link length is 1').max(100, 'Maximum Facebook Link length is 100').required('Facebook Link is required'),
  instagram_link: Yup.string().min(1, 'Minimum Instagram Link length is 1').max(100, 'Maximum Instagram Link length is 100').required('Instagram Link is required'),
  twitter_link: Yup.string().min(1, 'Minimum Twitter Link length is 1').max(100, 'Maximum Twitter Link length is 100').required('Twitter Link is required'),
  pinterest_link: Yup.string().min(1, 'Minimum Pinterest Link length is 1').max(100, 'Maximum Pinterest Link length is 100').required('Pinterest Link is required'),
  linkedin_link: Yup.string().min(1, 'Minimum Linkedin Link length is 1').max(100, 'Maximum Linkedin Link length is 100').required('Linkedin Link is required'),
  youtube_link: Yup.string().min(1, 'Minimum Youtube Link length is 1').max(100, 'Maximum Youtube Link length is 100').required('Youtube Link is required'),
});

export const socialLinksSchema = Yup.object().shape({
  facebook_link: Yup.string().min(1, 'Minimum Facebook Link length is 1').max(100, 'Maximum Facebook Link length is 100').required('Facebook Link is required'),
  instagram_link: Yup.string().min(1, 'Minimum Instagram Link length is 1').max(100, 'Maximum Instagram Link length is 100').required('Instagram Link is required'),
  twitter_link: Yup.string().min(1, 'Minimum Twitter Link length is 1').max(100, 'Maximum Twitter Link length is 100').required('Twitter Link is required'),
  pinterest_link: Yup.string().min(1, 'Minimum Pinterest Link length is 1').max(100, 'Maximum Pinterest Link length is 100').required('Pinterest Link is required'),
  linkedin_link: Yup.string().min(1, 'Minimum Linkedin Link length is 1').max(100, 'Maximum Linkedin Link length is 100').required('Linkedin Link is required'),
  youtube_link: Yup.string().min(1, 'Minimum Youtube Link length is 1').max(100, 'Maximum Youtube Link length is 100').required('Youtube Link is required'),
});

export const basicInformationSchema = Yup.object().shape({
  alternate_email: Yup.string().min(1, 'Minimum email length is 1').max(190, 'Maximum email length is 190').required('Email is required'),
  phone_number: Yup.string().min(1, 'Minimum Phone Number length is 1').max(15, 'Maximum Phone Number length is 15').required('Phone Number is required'),
  alternate_phone_number: Yup.string().min(1, 'Alternate Phone Number No length is 1').max(15, 'Maximum Alternate Phone Number length is 15').required('Alternate Phone Number is required'),
  whatsapp_number: Yup.string().min(1, 'Minimum Whatsapp Number No length is 1').max(15, 'Maximum Whatsapp Number No length is 15').required('Whatsapp Number is required'),
  address: Yup.string().min(5, 'Minimum Address length is 5').max(300, 'Maximum Address length is 300').required('Address is required'),
});

export const companyInformationSchema = Yup.object().shape({
  customer_care_no: Yup.string().min(1, 'Minimum Customer Care No length is 1').max(15, 'Maximum Customer Care No length is 15').required('Customer Care No is required'),
  company_name: Yup.string().min(1, 'Minimum Company Name length is 1').max(50, 'Maximum Company Name length is 50').required('Company Name is required'),
  ceo_name: Yup.string().min(1, 'Minimum CEO Name length is 1').max(50, 'Maximum CEO Name length is 50').required('CEO Name is required'),
  description: Yup.string().min(5, 'Minimum Description length is 5').max(500, 'Maximum Description length is 500').required('Description is required'),
  company_address: Yup.string().min(5, 'Minimum Address length is 5').max(300, 'Maximum Address length is 300').required('Company Address is required'),
  pin_code: Yup.string().required('Pin Code is required'),
  map_location_1: Yup.string().min(10, 'Minimum Address length is 10').required('Location 1 is required'),
  map_location_2: Yup.string().min(10, 'Minimum Address length is 10').required('Location 2 is required'),
});

export const websiteConfigurationSchema = Yup.object().shape({
  customer_care_no: Yup.string().min(1, 'Minimum Customer Care No length is 1').max(15, 'Maximum Customer Care No length is 15').required('Customer Care No is required'),
  website_email: Yup.string().min(1, 'Minimum email length is 1').max(190, 'Maximum email length is 190').required('Website email is required'),
});

export const storeProductSchema = Yup.object().shape({
  category_id: Yup.string().required('Category id is required'),
  sub_category_id: Yup.string().required('Sub Category id is required'),
  product_name: Yup.string().min(1, 'Minimum Product name length is 1').max(100, 'Maximum Product name length is 100').required('Product name is required'),
  product_code: Yup.string().min(1, 'Minimum Product code length is 1').max(15, 'Maximum Product code length is 50').required('Product code is required'),
  price: Yup.number().min(1, 'Minimum Product price length is 1').max(1000000, 'Maximum Product price length is 1000000').required('Product price is required'),
  selling_price: Yup.number().min(1, 'Minimum Product selling price length is 1').max(1000000, 'Maximum Product selling price length is 1000000').required('Product selling price is required'),
  description: Yup.string().min(1, 'Minimum Product description length is 1').max(190, 'Maximum Product description length is 190').required('Product description is required'),
  quantity: Yup.number().min(1, 'Minimum Product quantity length is 1').max(1000000, 'Maximum Product quantity length is 1000000').required('Product quantity is required'),
  min_order_quantity: Yup.number().min(1, 'Minimum Product order quantity length is 1').max(1000000, 'Maximum Product order quantity length is 1000000').required('Product order quantity is required'),
  meta_title: Yup.string().min(1, 'Minimum Meta title length is 1').max(190, 'Maximum Meta title length is 190').required('Meta title is required'),
  meta_description: Yup.string().min(1, 'Minimum Meta description length is 1').max(190, 'Maximum Meta description length is 190').required('Meta description is required'),
  meta_keyword: Yup.string().min(1, 'Minimum Meta keyword length is 1').max(190, 'Maximum Meta keyword length is 190').required('Meta keyword is required'),
  // product_images: Yup.array().min(1, 'Product Images is required').required('Product Images is required'),
  // product_attributes: Yup.array().min(1, 'Product Attribute is required')
});

export const updateProductSchema = Yup.object().shape({
  category_id: Yup.string().required('Category id is required'),
  sub_category_id: Yup.string().required('Sub Category id is required'),
  product_name: Yup.string().min(1, 'Minimum Product name length is 1').max(100, 'Maximum Product name length is 100').required('Product name is required'),
  product_code: Yup.string().min(1, 'Minimum Product code length is 1').max(15, 'Maximum Product code length is 50').required('Product code is required'),
  price: Yup.number().min(1, 'Minimum Product price length is 1').max(1000000, 'Maximum Product price length is 1000000').required('Product price is required'),
  selling_price: Yup.number().min(1, 'Minimum Product selling price length is 1').max(1000000, 'Maximum Product selling price length is 1000000').required('Product selling price is required'),
  description: Yup.string().min(1, 'Minimum Product description length is 1').max(190, 'Maximum Product description length is 190').required('Product description is required'),
  quantity: Yup.number().min(1, 'Minimum Product quantity length is 1').max(1000000, 'Maximum Product quantity length is 1000000').required('Product quantity is required'),
  min_order_quantity: Yup.number().min(1, 'Minimum Product order quantity length is 1').max(1000000, 'Maximum Product order quantity length is 1000000').required('Product order quantity is required'),
  meta_title: Yup.string().min(1, 'Minimum Meta title length is 1').max(190, 'Maximum Meta title length is 190').required('Meta title is required'),
  meta_description: Yup.string().min(1, 'Minimum Meta description length is 1').max(190, 'Maximum Meta description length is 190').required('Meta description is required'),
  meta_keyword: Yup.string().min(1, 'Minimum Meta keyword length is 1').max(190, 'Maximum Meta keyword length is 190').required('Meta keyword is required'),
});


export const teamSchema = Yup.object().shape({
  member_name: Yup.string().required('Name is required'),
  member_position: Yup.string().required('Position is required'),
  member_image: Yup.mixed().required("Image is required"),
});

export const teamUpdateSchema = Yup.object().shape({
  member_name: Yup.string().required('Name is required'),
  member_position: Yup.string().required('Position is required'),
  // member_image: Yup.mixed().required("Image is required"),
});

export const updatePortfolioSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(3, "Minimum title length is 3")
    .max(200, "Maximum title length is 200")
    .required("Portfolio title is required"),

  description: Yup.string()
    .trim()
    .transform((value) => (value === "" ? null : value))
    .notRequired()
    .min(10, "Minimum description length is 10")
    .max(2000, "Maximum description length is 2000")
    .nullable(),

  category: Yup.string()
    .trim()
    .min(3, "Minimum category length is 3")
    .max(100, "Maximum category length is 100")
    .required("Portfolio category is required"),

  status: Yup.string()
    .oneOf(["draft", "published", "completed"], "Invalid status")
    .notRequired(),

  start_date: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Invalid start date")
    .notRequired(),

  end_date: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Invalid end date")
    .notRequired()
    .test(
      "is-after-start",
      "End date must be after start date",
      function (value) {
        const { start_date } = this.parent;
        if (!value || !start_date) return true;
        return new Date(value) >= new Date(start_date);
      }
    ),

  project_url: Yup.string()
    .trim()
    .matches(
      /^((https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/.*)?)$/,
      { message: "Please enter a valid URL (e.g., https://example.com)", excludeEmptyString: true }
    )
    .nullable()
    .notRequired(),

  tags: Yup.array()
    .of(Yup.string().trim())
    .nullable(),

  media: Yup.array().nullable(),
});

export const bulkOfferSchema = Yup.object().shape({
  company_id: Yup.string().required("Company selection is required"),
  category_id: Yup.string().required("Category is required"),
  name: Yup.string().required("Product name is required"),
  brief_description: Yup.string().required("Brief description is required"),
  sku: Yup.string().required("Product SKU is required"),
  company_type: Yup.string().required("Company type is required"),
  mrp: Yup.number().required("MRP is required").positive("Must be positive"),
  offer_price: Yup.number().required("Offer price is required").positive("Must be positive")
    .test("is-less-than-mrp", "Offer price must be less than MRP", function (value) {
      const { mrp } = this.parent;
      return !value || !mrp || Number(value) < Number(mrp);
    }),
  lot_size: Yup.number().required("Lot size is required").min(1, "Minimum 1"),
  available_qty: Yup.number().required("Available quantity is required").min(1, "Minimum 1")
    .test("is-at-least-lot-size", "Available quantity must be greater than or equal to lot size", function (value) {
      const { lot_size } = this.parent;
      return !value || !lot_size || Number(value) >= Number(lot_size);
    })
    .test("is-multiple-of-lot-size", "Available quantity must be a multiple of lot size", function (value) {
      const { lot_size } = this.parent;
      return !value || !lot_size || (Number(value) % Number(lot_size) === 0);
    }),
  start_date: Yup.date().required("Start date is required"),
  end_date: Yup.date().required("End date is required")
    .test("is-after-start", "End date must be after start date", function (value) {
      const { start_date } = this.parent;
      if (!value || !start_date) return true;
      return new Date(value) > new Date(start_date);
    }),
  billing_from: Yup.string().required("Billing from is required"),
  booking_deposit: Yup.number().required("Booking deposit is required").min(0, "Must be at least 0"),
  payment_credit_policy: Yup.string().nullable().notRequired(),
  product_description: Yup.string().required("Product description is required"),
  variations: Yup.string().required("Variations info is required"),
  features: Yup.string().required("Key features are required"),
  delivery_taken_before: Yup.string().nullable().notRequired(),
  delivery_charges: Yup.string().nullable().notRequired(),
  delivery_start_date: Yup.date().nullable().notRequired(),
  country_id: Yup.string().required("Country of origin is required"),
  plant_address: Yup.string().required("Plant address is required"),
  marketed_by: Yup.string().required("Marketed by is required"),
  manufactured_by: Yup.string().required("Manufactured by is required"),
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  serviceability_type: Yup.string().oneOf(["pan_india", "states", "cities"], "Invalid serviceability type").required("Serviceability type is required"),
  serviceable_ids: Yup.array().when("serviceability_type", {
    is: (val) => val === "states" || val === "cities",
    then: (schema) => schema.min(1, "Selection is required").required("Selection is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  existingImages: Yup.array().test("at-least-one-image", "At least one offer image is required", function (value) {
    const { images } = this.parent;
    return (value && value.length > 0) || (images && images.length > 0);
  }),
  existingAPlusImages: Yup.array().min(1, "At least one A+ photo is required"),
});

export const addProfileSuggestionCardSchema = Yup.object().shape({
  key: Yup.string()
    .trim()
    .required("Key is required"),
  title: Yup.string()
    .trim()
    .nullable()
    .notRequired(),
  description: Yup.string()
    .trim()
    .nullable()
    .notRequired(),
  cta_text: Yup.string()
    .trim()
    .nullable()
    .notRequired(),
  cta_action: Yup.string()
    .trim()
    .nullable()
    .notRequired(),
  is_active: Yup.boolean().required("Status is required"),
  image: Yup.mixed().when("hasExistingImage", {
    is: false,
    then: (schema) =>
      schema
        .required("Media file is required")
        .test(
          "fileType",
          "Only JPG, PNG, WEBP, MP4, WEBM, and MOV files are allowed",
          (value) =>
            value &&
            [
              "image/jpeg",
              "image/png",
              "image/webp",
              "video/mp4",
              "video/webm",
              "video/quicktime",
            ].includes(value.type)
        )
        .test(
          "fileSize",
          "File size must be less than 20MB",
          (value) => value && value.size <= 20 * 1024 * 1024
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});
